import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useStore } from '../../hooks/useStore';
import { Logo } from '../../lib/components';
import { HeaderBar } from '../../lib/components/compound/surfaces/headerBar';
import { AvatarMenu } from './avatarMenu';
import { useCompanySettings } from '../../queries/company';
import { CrossIcon } from '../../lib/icons';
import { useCardOnboarding, useListCardApprovals } from '../../queries/pxtraCard';
import {
  needsCompanyAction,
  useOnboardingOutcomeNotice
} from '../../lib/components/compound/pxtraCardOnboarding';
import { usePxtraCardEnabled } from '../../hooks/usePxtraCardEnabled';

type Props = { hideMenuLinks?: boolean };

export type BaseAppbarProps = Props;

export const BaseAppbar: FC<Props> = observer(({ hideMenuLinks = false }) => {
  const { i18n } = useTranslation();
  const { authenticationStore, companyStore } = useStore();
  const companyId = companyStore.company?.id ?? 'MISSING_ID';

  const { data: companySettings } = useCompanySettings(companyId);
  const isBonusStandalone = companyStore.company?.is_bonus_standalone ?? false;

  const { isCardEnabled } = usePxtraCardEnabled();
  const { data: onboarding } = useCardOnboarding(companyId);
  useOnboardingOutcomeNotice(companyId, onboarding);
  const { data: cardApprovals } = useListCardApprovals(companyId);
  const hasOpenApprovals = !!cardApprovals?.results.some((request) => request.status === 'open');
  const benefitsHasTodo = isCardEnabled && (needsCompanyAction(onboarding) || hasOpenApprovals);

  useEffect(() => {
    if (!authenticationStore.language) return;

    i18n.changeLanguage(authenticationStore.language);
  }, [authenticationStore.language]);
  return (
    <HeaderBar
      logo={
        <div className="flex items-center">
          {companySettings?.logo && (
            <div className="flex items-center">
              <div className="h-8 w-fit max-w-[200px] rounded-lg bg-grey-50 border-grey-100 p-2 mr-1">
                <img
                  src={companySettings?.logo?.url}
                  alt=""
                  className="h-full max-w-[184px] m-auto object-contain"
                />
              </div>
              <CrossIcon className="text-grey-100 h-8 w-8" />
            </div>
          )}
          <Link tabIndex={-1} to={isBonusStandalone ? '/bonus' : '/'} className="!-pl-12">
            <Logo className={companySettings?.logo ? 'h-8 mx-0' : 'h-12 mx-8'} />
          </Link>
        </div>
      }
      linkItemConfig={
        hideMenuLinks
          ? []
          : [
              ...(!isBonusStandalone ? [{ text: 'DASHBOARD' as const, route: 'dashboard' }] : []),
              ...(!isBonusStandalone
                ? [{ text: 'BENEFITS' as const, route: 'benefits', hasTodo: benefitsHasTodo }]
                : []),
              { text: 'BONUS' as const, route: 'bonus' },
              { text: 'EMPLOYEES', route: 'employees' },
              { text: 'COMPANY', route: 'company' },
              ...(!isBonusStandalone ? [{ text: 'RECEIPTS' as const, route: 'receipts' }] : []),
              { text: 'IMPEX', route: 'exports/overview', activeRoutes: ['imports', 'exports'] }
            ]
      }
      menuComponent={<AvatarMenu />}
    />
  );
});
